/**
 * Shared parsing helpers for the European Central Bank's public euro
 * reference-rate feeds. All are static XML files served from
 * www.ecb.europa.eu — no API key, and (per ECB's own publishing docs)
 * intended for exactly this kind of embedding.
 *
 *   - Daily snapshot (today's rate for ~28 currencies vs EUR):
 *     https://www.ecb.europa.eu/stats/eurofxref/eurofxref-daily.xml
 *   - Last 90 calendar days, same shape, one <Cube time="..."> per day:
 *     https://www.ecb.europa.eu/stats/eurofxref/eurofxref-hist-90d.xml
 *   - Full history back to 1999 (same shape, ~9,500 day-cubes — multiple
 *     MB, so we don't fetch this client-side by default; see note in
 *     CurrencyHistoryChart.js):
 *     https://www.ecb.europa.eu/stats/eurofxref/eurofxref-hist.xml
 *
 * Note: the "RSS feeds" link on ECB's exchange-rate page points to their
 * general news/press RSS hub (ecb.europa.eu/home/html/rss.en.html), not a
 * currency-specific feed — there isn't a separate RSS feed for rates, just
 * these XML files, so that's what both the ticker and the history chart
 * use directly.
 */

export const ECB_DAILY_URL = 'https://www.ecb.europa.eu/stats/eurofxref/eurofxref-daily.xml';
export const ECB_HIST_90D_URL = 'https://www.ecb.europa.eu/stats/eurofxref/eurofxref-hist-90d.xml';

export const CURRENCY_NAMES = {
  USD: 'US Dollar',
  JPY: 'Japanese Yen',
  BGN: 'Bulgarian Lev',
  CZK: 'Czech Koruna',
  DKK: 'Danish Krone',
  GBP: 'British Pound',
  HUF: 'Hungarian Forint',
  PLN: 'Polish Zloty',
  RON: 'Romanian Leu',
  SEK: 'Swedish Krona',
  CHF: 'Swiss Franc',
  ISK: 'Icelandic Krona',
  NOK: 'Norwegian Krone',
  TRY: 'Turkish Lira',
  AUD: 'Australian Dollar',
  BRL: 'Brazilian Real',
  CAD: 'Canadian Dollar',
  CNY: 'Chinese Yuan',
  HKD: 'Hong Kong Dollar',
  IDR: 'Indonesian Rupiah',
  ILS: 'Israeli Shekel',
  INR: 'Indian Rupee',
  KRW: 'South Korean Won',
  MXN: 'Mexican Peso',
  MYR: 'Malaysian Ringgit',
  NZD: 'New Zealand Dollar',
  PHP: 'Philippine Peso',
  SGD: 'Singapore Dollar',
  THB: 'Thai Baht',
  ZAR: 'South African Rand',
};

export function currencyName(code) {
  return CURRENCY_NAMES[code] || code;
}

/** Parses eurofxref-daily.xml into [{ currency, rate }], one per currency. */
export function parseDailyRates(xmlText) {
  const doc = new DOMParser().parseFromString(xmlText, 'text/xml');
  if (doc.querySelector('parsererror')) throw new Error('Malformed ECB XML response');

  return Array.from(doc.querySelectorAll('Cube[currency]'))
    .map((node) => ({
      currency: node.getAttribute('currency'),
      rate: parseFloat(node.getAttribute('rate')),
    }))
    .filter((r) => r.currency && !Number.isNaN(r.rate));
}

/**
 * Parses a multi-day ECB feed (hist-90d.xml or hist.xml) into an ascending
 * [{ date, rate }] time series for a single currency code.
 */
export function parseHistoricalSeries(xmlText, currencyCode) {
  const doc = new DOMParser().parseFromString(xmlText, 'text/xml');
  if (doc.querySelector('parsererror')) throw new Error('Malformed ECB XML response');

  const series = [];
  doc.querySelectorAll('Cube[time]').forEach((dayNode) => {
    const date = dayNode.getAttribute('time');
    const currencyNode = Array.from(dayNode.children).find(
      (c) => c.getAttribute('currency') === currencyCode
    );
    if (currencyNode) {
      const rate = parseFloat(currencyNode.getAttribute('rate'));
      if (date && !Number.isNaN(rate)) series.push({ date, rate });
    }
  });

  return series.sort((a, b) => new Date(a.date) - new Date(b.date));
}

/** Guards against building fetch URLs / selectors from arbitrary query-string input. */
export function isValidCurrencyCode(code) {
  return typeof code === 'string' && /^[A-Z]{3}$/.test(code);
}
